/* eslint-disable @typescript-eslint/no-unused-vars */

import { useEffect, useState } from 'react';
import { Alert, Autocomplete, Box, Button, Chip, Grid, LinearProgress, Paper, Snackbar, SnackbarCloseReason, TextField } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { VTextField,  VForm, useVForm, IVFormErrors, VSwitch, VTextFieldNumber } from '../../../shared/forms';
import { FerramentasDeDetalhe } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { ContaBancariaService } from '../../../shared/services/api/cadastros/contaBancaria/ContaBancariaService';
import { ProdutorService } from '../../../shared/services/api/cadastros/ProdutorService';

interface IFormData {
  nomebanco: string;
  agencia: string;
  numeroconta: string;
  status: boolean;
  titular: string;
  ir: boolean;
  banco: string;
}

interface IProdutor {
  idprodutor: number;
  nome: string;
  cpf: string;
}

const formValidationSchema: yup.ObjectSchema<IFormData> = yup.object().shape({
  nomebanco: yup.string().required().max(30),
  agencia: yup.string().required().max(4),
  numeroconta: yup.string().required().max(16),
  status: yup.boolean().optional().default(true),
  titular: yup.string().required(),
  ir: yup.boolean().required(),
  banco: yup.string().required().max(3),
  idir: yup.number().optional().default(0),
});

export const DetalheDeContaBancaria: React.FC = () => {
  const { id = 'novo' } = useParams<'id'>(); 
  const navigate = useNavigate();
  const { formRef, save, saveAndClose } = useVForm();
  const [produtores, setProdutores] = useState<IProdutor[]>([]);
  const [titulares, setTitulares] = useState<IProdutor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [descricao, setDescricao] = useState('');
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error'  | 'info'  | 'success'>('warning');  

  const produtor: IProdutor[] = [];

  const fixedOptions = produtor;
  const [value, setValue] = useState([...produtor]);
        
  const [openConfirm, setOpenConfirm] = useState(false);
          
  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event, 
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
    if(tms === 'success'){ 
      navigate('/contabancaria');
    }
  };
        
  const handleDelete = () => {
    setOpenConfirm(true); 
  };
        
  const handleConfirmDelete = () => {
    ContaBancariaService.deleteById(id)
      .then(result => {
        if (result instanceof Error) {
          setOpenConfirm(false);
          setTms('error');
          setMsg(result.message);
          setOpenmsg(true);
        } else {
          setOpenConfirm(false);
          setTms('success');
          setMsg('Registro apagado com sucesso!');
          setOpenmsg(true);
        }
      });
  };
        
  const handleCancelDelete = () => {
    setOpenConfirm(false);
  };

  useEffect(() => {
    if (id !== 'novo') {
      setIsLoading(true);
      ContaBancariaService.getById(id)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setDescricao(result.titular);
            formRef.current?.setData(result);
            setValue(result.titulares);
          }
        });
    } else {
      formRef.current?.setData({
        nomebanco: '',
        agencia: '',
        numeroconta: '',
        status: true,
        titular: '',
        ir: false,
        banco: '',
      });
    }
    ProdutorService.getAlllist(1, '')
      .then((result) => {
        setIsLoading(false);
        if (result instanceof Error) {
          //
        } else {
          setProdutores(result.data);
        }
      });
  }, [id]);
  
  const handleSave = (dados: IFormData) => {
    formValidationSchema.
      validate(dados, { abortEarly: false })
      .then((dadosValidados) => {
        setIsLoading(true);
        if (id === 'novo') {
          ContaBancariaService
            .create(dadosValidados, titulares)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Lançamento Realizado com sucesso!');
                setOpenmsg(true);
              }
            });
        } else {
          ContaBancariaService
            .updateById(id, dadosValidados, titulares)
            .then((result) => {
              setIsLoading(false);
              if (result instanceof Error) {
                setTms('error');
                setMsg(result.message);
                setOpenmsg(true);
              } else {
                setTms('success');
                setMsg('Alteração concluída com sucesso!');
                setOpenmsg(true);
              }
            });
        }
      })
      .catch((errors: yup.ValidationError) => {
        const validationErrors: IVFormErrors = {};
        errors.inner.forEach(error => {
          if (!error.path) return;
          validationErrors[error.path] = error.message;
        });
        formRef.current?.setErrors(validationErrors);
      });
  };

  return (
    <LayoutBaseDePagina
      titulo={id === 'novo' ? 'Nova Conta Bancária' : descricao}
      barraDeFerramentas={
        <FerramentasDeDetalhe
          textoBotaoNovo='Novo'
          mostrarBotaoSalvar
          mostrarBotaoSalvarEFechar={false}
          mostrarBotaoSalvarCarregando = {isLoading}
          mostrarBotaoSalvarEFecharCarregando = {isLoading}
          mostrarBotaoNovo={id !== 'novo'}
          mostrarBotaoApagar={id !== 'novo'}
          mostrarBotaoApagarCarregando = {isLoading}
          mostrarBotaoImpressao = {false}
          aoClicarEmSalvar={save}
          aoClicarEmSalvarEFechar={saveAndClose}
          aoClicarEmVoltar={() => navigate('/contabancaria')}
          mostrarBotaoVoltarCarregando = {isLoading}
          aoClicarEmApagar={() => handleDelete()}
          aoClicarEmNovo={() => navigate('/contabancaria/detalhe/novo')}
          mostrarBotaoNovoCarregando = {isLoading}
        />
      }
    >
      <VForm ref={formRef} onSubmit={handleSave}>
        <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={openmsg}  autoHideDuration={900} onClose={handleCloseMsg}>
          <Alert
            onClose={handleCloseMsg}
            severity={tms}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {msg}
          </Alert>
        </Snackbar>
        <Snackbar open={openConfirm} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
          <Alert
            severity="warning"  
            variant="filled"
            sx={{ display: 'flex', alignItems: 'center', width: '100%' }}
            action={
              <>
                <Button color="inherit" size="small" onClick={handleConfirmDelete}>
              Sim
                </Button>
                <Button color="inherit" size="small" onClick={handleCancelDelete}>
              Nâo
                </Button>
              </>
            }
          >
            Tem certeza que deseja excluir este registro?
          </Alert>
        </Snackbar>
        <Box margin={1} display="flex" flexDirection="column" component={Paper} variant="outlined">

          <Grid container direction="column" padding={2} spacing={2}>

            {isLoading && (
              <Grid item>
                <LinearProgress variant='indeterminate' />
              </Grid>
            )}
            <Grid container item direction="row" >
              <Grid item xs={12}  sm={12} md={6} lg={8} xl={8}> 
                <Grid container item direction="row" spacing={2}>
                  <Grid item xs={12} sm={12} md={6} lg={12} xl={12}>          
                    <VTextField
                      fullWidth
                      size='small'
                      name='titular'
                      disabled={isLoading}
                      label='Descrição da Conta'
                      onChange={e => setDescricao(e.target.value)}
                    />
                  </Grid>
                  <Grid item xs={3} sm={3} md={2} lg={12} xl={12}>
                    <Autocomplete
                      size='small'
                      multiple
                      id="fixed-tags-demo"
                      value={value}
                      onChange={(event, newValue) => {
                        setValue([
                          ...fixedOptions,
                          ...newValue,
                        ]);
                        setTitulares(newValue);
                      }}
                      options={produtores}
                      getOptionLabel={(option) => option.nome}
                      renderTags={(tagValue, getTagProps) =>
                        tagValue.map((option, index) => {
                          const { key, ...tagProps } = getTagProps({ index });
                          return (
                            <Chip
                              key={key}
                              label={option.nome}
                              {...tagProps}
                              disabled={fixedOptions.includes(option)}
                            />
                          );
                        })
                      }
                      isOptionEqualToValue={(option, value) => option.idprodutor === value.idprodutor}
                      renderInput={(params) => (
                        <TextField  {...params} size='small' label="Titulares da Conta" placeholder="Produtores Titulares" />
                      )}
                    />
                  </Grid> 
                  <Grid item xs={12} sm={12} md={6} lg={4} xl={4}>
                    <VTextField
                      fullWidth
                      size='small'
                      name='nomebanco'
                      disabled={isLoading}
                      label='Banco'
                    />
                  </Grid> 
                  <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>          
                    <VTextFieldNumber
                      fullWidth
                      size='small'
                      name='banco'
                      disabled={isLoading}
                      label='Nº do Banco'
                    />
                  </Grid>
                  <Grid item xs={6} sm={6} md={3} lg={1.5} xl={1.5}>          
                    <VTextField
                      fullWidth
                      size='small'
                      name='agencia'
                      disabled={isLoading}
                      label='Agência'
                    />
                  </Grid>
                  <Grid item xs={12} sm={12} md={6} lg={2} xl={2}>          
                    <VTextField
                      fullWidth
                      size='small'
                      name='numeroconta'
                      disabled={isLoading}
                      label='Nº da Conta'
                    />
                  </Grid>
              
                  <Grid item xs={3} sm={3} md={2} lg={1} xl={1}>
                    {(id != 'novo') && (
                      <VTextField
                        fullWidth
                        size='small'
                        name='idir'
                        disabled={isLoading}
                        label='Cód IR'
                      />
                    )}
                  </Grid> 
                  <Grid item xs={3} marginTop={1} sm={3} md={2} lg={1} xl={0.8}>
                    <VSwitch
                      size='medium'
                      name='ir'
                      label='IR'
                      fdisabled={isLoading}
                      aliamento='start'
                    />
                  </Grid> 
            
                  <Grid item marginTop={1}  xs={3} sm={3} md={2} lg={1} xl={0.5}>
                    {(id != 'novo') && (
                      <VSwitch
                        size='medium'
                        name='status'
                        label='Ativo'
                        fdisabled={isLoading}
                        aliamento='start'
                      />
                    )}
                  </Grid> 
                 
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </VForm> 

    </LayoutBaseDePagina>
  );
};